// FIFO цената на ЛИПСИТЕ при инвентаризация (изнесена от
// `useStocktakeRowsActions`): партидите се теглят лениво — само за продуктите,
// преброени под търсеното, — и цената е претеглената по реда на партидите за
// липсващото количество. Докато партидите се зареждат, редът пада на средната
// цена (решава `computeRow`).
// Редовете с „Намерено" 0 (напр. след „Занули склада") НЕ теглят партиди,
// когато са повече от `ZERO_ROWS_LOTS_LIMIT`: иначе стотици паралелни
// `GET /inventory/lots`. За тях стойността е по средната цена — ориентир;
// точната FIFO стойност я смята бекендът при записа.
import { useCallback, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { getLots, type Lot } from "../../../../api/inventory";
import {
  DIFF_EPSILON,
  isRowCounted,
  parseQtyExpression,
  type StocktakeRowDraft,
} from "./stocktakeWorksheetShared";

export const ZERO_ROWS_LOTS_LIMIT = 30;

type Params = {
  warehouseId: number | null;
  rows: Record<number, StocktakeRowDraft>;
  systemQtyOf: (productId: number) => number;
};

export const useStocktakeFifoPrices = ({ warehouseId, rows, systemQtyOf }: Params) => {
  const shortageProductIds = useMemo(() => {
    const ids: number[] = [];
    const zeroCountedIds: number[] = [];
    for (const [productIdText, row] of Object.entries(rows)) {
      if (!isRowCounted(row)) continue;
      const productId = +productIdText;
      const counted = parseQtyExpression(row.counted);
      if (counted - systemQtyOf(productId) >= -DIFF_EPSILON) continue;
      (counted <= DIFF_EPSILON ? zeroCountedIds : ids).push(productId);
    }
    return zeroCountedIds.length > ZERO_ROWS_LOTS_LIMIT ? ids : [...ids, ...zeroCountedIds];
  }, [rows, systemQtyOf]);
  const lotsQueries = useQueries({
    queries: shortageProductIds.map((productId) => ({
      queryKey: ["inventory", "lots", warehouseId, productId],
      queryFn: () => getLots(warehouseId as number, productId),
      enabled: !!warehouseId,
    })),
  });
  // Подпис на заредените партиди — за да не се преизчислява всичко на всеки рендер
  const lotsSignature = lotsQueries.map((q) => q.dataUpdatedAt).join("|");
  const lotsByProduct = useMemo(() => {
    const map = new Map<number, Lot[]>();
    shortageProductIds.forEach((productId, index) => {
      const data = lotsQueries[index]?.data;
      if (data) map.set(productId, data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortageProductIds, lotsSignature]);

  // Претеглена FIFO цена за `needed` количество (по реда на партидите);
  // null = партидите още не са заредени или няма такива
  return useCallback(
    (productId: number, needed: number): number | null => {
      const lots = lotsByProduct.get(productId);
      if (!lots || lots.length === 0) return null;
      if (needed <= 0) return lots[0].unitPrice;
      let remaining = needed;
      let cost = 0;
      let consumed = 0;
      for (const lot of lots) {
        if (remaining <= 0) break;
        const take = Math.min(lot.remaining, remaining);
        cost += take * lot.unitPrice;
        consumed += take;
        remaining -= take;
      }
      return consumed > 0 ? cost / consumed : lots[0].unitPrice;
    },
    [lotsByProduct],
  );
};
